import React from 'react';

interface CertificateProps {
  uibfsLogoUrl?: string;
  financeTrustLogoUrl?: string;
  registrarSignatureUrl?: string;
  ceoSignatureUrl?: string;
  recipientName?: string;
  eventName?: string;
  theme?: string;
  trainingDate?: string;
}

const Certificate: React.FC<CertificateProps> = ({
  uibfsLogoUrl,
  financeTrustLogoUrl,
  registrarSignatureUrl,
  ceoSignatureUrl,
  recipientName = "OBADIAH LAKONY",
  eventName = "Microfinance & SACCOs Governance forum",
  theme = "Digital Transformation & Sustainability in Microfinannce",
  trainingDate = "Thursday 5 December, 2023"
}) => {
  const palette = {
    outerBackground: '#f3f4f6',
    cardBackground: '#ffffff',
    accentNavy: '#0b1e5d',
    accentGray: '#4b5563',
    accentLightGray: '#9ca3af',
    divider: '#d1d5db',
    bodyText: '#111827',
  }

  return (
    <div className="flex items-center justify-center" style={{ backgroundColor: palette.outerBackground, color: palette.bodyText }}>
      <div className="relative w-full max-w-4xl overflow-hidden" style={{ backgroundColor: palette.cardBackground, boxShadow: '0 25px 50px -12px rgba(30, 41, 59, 0.25)' }}>
        {/* Top left diagonal corner */}
        <div
          className="absolute top-0 left-0 w-0 h-0 border-t-[650px] border-r-[400px] border-r-transparent"
          style={{ borderTopColor: palette.accentNavy }}
        />
        {/* Bottom right corner accent */}
        <div
          className="absolute bottom-0 right-0 w-0 h-0 border-b-[650px] border-l-[400px] border-l-transparent"
          style={{ borderBottomColor: palette.accentNavy }}
        />

        <div className="m-4 relative" style={{ backgroundColor: palette.cardBackground }}>
          {/* Main content */}
          <div className="relative p-8">
            {/* Logos */}
            <div className="flex justify-between items-start mb-16">
              {/* UIBFS Logo */}
              <div className="w-32">
                <img
                  src={uibfsLogoUrl || "/images/uibfs-logo.png"}
                  alt="UIBFS Logo"
                  className="w-full h-auto"
                />
              </div>

              {/* Finance Trust Bank Logo */}
              {financeTrustLogoUrl && (
                <div className="w-32">
                  <img src={financeTrustLogoUrl} alt="Finance Trust Bank Logo" className="w-full h-auto" />
                </div>
              )}
            </div>

            {/* Certificate heading */}
            <div className="text-center mb-8">
              <h1 className="text-6xl font-bold" style={{ color: palette.accentNavy }}>CERTIFICATE</h1>
              <h2 className="text-4xl mb-2 font-light" style={{ color: palette.accentGray }}>of Participation</h2>
              <p className="text-xl" style={{ color: palette.accentGray }}>This is to certify that</p>
            </div>

            {/* Recipient name */}
            <div className="text-center mb-2">
              <div className="border-b-2 mx-auto max-w-2xl" style={{ borderColor: palette.divider }}>
                <h3 className="text-4xl font-bold" style={{ color: palette.accentNavy }}>{recipientName}</h3>
              </div>
            </div>

            {/* Participation details */}
            <div className="text-center mb-8">
              <p className="text-xl mb-8" style={{ color: palette.bodyText }}>
                successfully participated in the second annual
              </p>
              <h4 className="text-3xl font-bold mb-8" style={{ color: palette.accentNavy }}>
                {eventName}
              </h4>
              <p className="text-lg mb-2 font-light" style={{ color: palette.bodyText }}>
                under the theme: <span className="font-bold">"{theme}"</span>
              </p>
              <p className="text-xl mt-8" style={{ color: palette.bodyText }}>offered by</p>
              <p className="text-xl mt-4" style={{ color: palette.bodyText }}>
                The Uganda Institute of Banking and Financial Services
              </p>
              <p className="text-xl" style={{ color: palette.bodyText }}>
                Training dates: {trainingDate}
              </p>
            </div>

            {/* Signatures section */}
            <div className="flex justify-between items-end mt-16 relative">
              {/* Registrar signature */}
              <div className="text-center flex-1">
                <div className="border-t-2 pt-2 mx-8" style={{ borderColor: palette.divider }}>
                  {registrarSignatureUrl ? (
                    <img src={registrarSignatureUrl} alt="Registrar Signature" className="h-12 mx-auto mb-1" />
                  ) : (
                    <div className="text-2xl mb-1 italic" style={{ color: palette.accentLightGray }}>
                      [Signature]
                    </div>
                  )}
                  <p className="font-bold text-sm" style={{ color: palette.accentNavy }}>Registrar</p>
                  <p className="text-xs" style={{ color: palette.accentGray }}>
                    The Uganda Institute of<br />Banking and Financial Services
                  </p>
                </div>
              </div>

              {/* ISO badge */}
              <div className="flex-shrink-0 mx-8">
                <div className="relative w-28 h-28">
                  <svg viewBox="0 0 120 120" className="w-full h-full">
                    <defs>
                      <radialGradient id="goldGradient">
                        <stop offset="0%" stopColor="#FDB931" stopOpacity="1" />
                        <stop offset="50%" stopColor="#F5A623" stopOpacity="1" />
                        <stop offset="100%" stopColor="#D68910" stopOpacity="1" />
                      </radialGradient>
                      <filter id="shadow">
                        <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.3" />
                      </filter>
                    </defs>

                    {/* Starburst background */}
                    {[...Array(20)].map((_, i) => {
                      const angle = (i * 18) * Math.PI / 180;
                      const x1 = 60 + 35 * Math.cos(angle);
                      const y1 = 60 + 35 * Math.sin(angle);
                      const x2 = 60 + 50 * Math.cos(angle + 0.157);
                      const y2 = 60 + 50 * Math.sin(angle + 0.157);
                      return <polygon key={i} points={`60,60 ${x1},${y1} ${x2},${y2}`} fill="#E09F1C" opacity="0.8" />;
                    })}

                    {/* Main circle */}
                    <circle cx="60" cy="60" r="45" fill="url(#goldGradient)" filter="url(#shadow)" />
                    <circle cx="60" cy="60" r="42" fill="#F5A623" />
                    <circle cx="60" cy="60" r="38" fill="#FDB931" />

                    {/* Inner ring */}
                    <circle cx="60" cy="60" r="36" fill="none" stroke="#D68910" strokeWidth="0.5" />

                    {/* Stars */}
                    <text x="60" y="32" textAnchor="middle" fontSize="8" fill="#000" fontWeight="bold">★★★★★</text>

                    {/* ISO text */}
                    <text x="60" y="45" textAnchor="middle" fontSize="5.5" fill="#000" fontWeight="600">●ISO 9001:2008●</text>

                    {/* CERTIFIED */}
                    <text x="60" y="60" textAnchor="middle" fontSize="9" fill="#000" fontWeight="bold">CERTIFIED</text>

                    {/* Checkmark */}
                    <text x="60" y="75" textAnchor="middle" fontSize="16" fill="#000" fontWeight="bold">✓</text>
                  </svg>
                </div>
              </div>

              {/* CEO signature */}
              <div className="text-center flex-1">
                <div className="border-t-2 pt-2 mx-8" style={{ borderColor: palette.divider }}>
                  {ceoSignatureUrl ? (
                    <img src={ceoSignatureUrl} alt="CEO Signature" className="h-12 mx-auto mb-1" />
                  ) : (
                    <div className="text-2xl mb-1 italic" style={{ color: palette.accentLightGray }}>
                      [Signature]
                    </div>
                  )}
                  <p className="font-bold text-sm" style={{ color: palette.accentNavy }}>Chief Executive Officer</p>
                  <p className="text-xs" style={{ color: palette.accentGray }}>
                    The Uganda Institute of<br />Banking and Financial Services
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom right watermark - large uibfs text */}
          <div className="absolute bottom-0 right-0 overflow-hidden pointer-events-none w-[372] h-[400px]">
            <img src="/images/certificate-bg.png" alt="UIBFS Watermark" className="w-full h-full opacity-[0.06]" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Certificate;
